import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  FilterValue,
  ActiveFilterMeta,
} from '@/components/DataTable';
import {
  listOrganizationGroupedAlerts,
  getOrganizationAlertFilterOptions,
} from '@kirin/api-client/api/alerts';
import { listOrganizationMembers } from '@kirin/api-client/api/members';
import { DISCOVERED_USERS_FILTER_VALUE } from '@kirin/api-client/contracts/alerts';
import type {
  AlertFilterOptionField,
  AlertVisibility,
  GroupedAlert,
  OrganizationScanAlertsSortField,
} from '@kirin/api-client/contracts/alerts';
import { apiClient } from '@/lib/api-client';
import { getUserDisplayName } from '@/lib/utils';

/**
 * Convert request filters to active filter metadata for response
 */
function toActiveFilterMeta(filters: FilterValue[]): ActiveFilterMeta[] {
  return filters.map(filter => ({
    field: filter.field,
    operator: filter.operator,
    values: filter.value,
  }));
}

/**
 * Type guard to check if response uses table engine format
 * Table engine responses have a 'data' field instead of 'alerts'
 */
function isTableEngineResponse(
  response: Awaited<ReturnType<typeof listOrganizationGroupedAlerts>>
): response is Awaited<ReturnType<typeof listOrganizationGroupedAlerts>> & {
  data: GroupedAlert[];
} {
  return 'data' in response;
}

function isLegacyAlertsResponse(
  response: Awaited<ReturnType<typeof listOrganizationGroupedAlerts>>
): response is Awaited<ReturnType<typeof listOrganizationGroupedAlerts>> & {
  alerts: GroupedAlert[];
} {
  return 'alerts' in response;
}

function transformLegacyToDataTableRespponse(
  response: Awaited<ReturnType<typeof listOrganizationGroupedAlerts>>,
  filters: FilterValue[]
): DataFetchResponse<GroupedAlert> {
  const alerts = (response as Record<'alerts', GroupedAlert[]>).alerts;

  const activeFilters = toActiveFilterMeta(filters);

  return {
    data: alerts,
    pagination: {
      page: response.pagination.page || 1,
      limit: response.pagination.limit,
      total: response.pagination.total,
      hasMore: response.pagination.hasMore,
    },
    meta: {
      activeFilters,
    },
  };
}

/**
 * Fetch organization alerts with pagination for the DataTable.
 *
 * `visibility` is the unified KIR-2512 selector that replaces the legacy
 * `includeMuted` / `includeExcluded` toggles. The backend gates each
 * suppression mechanism behind its respective feature flag (`alert_muting`,
 * `alert_ignore`); when both are off `visibility` is effectively a no-op
 * and the response shape stays byte-identical to today.
 */
export async function fetchOrganizationAlerts(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal,
  visibility?: AlertVisibility
): Promise<DataFetchResponse<GroupedAlert>> {
  const sortField = params.sort?.field as
    | OrganizationScanAlertsSortField
    | undefined;
  const sortOrder = params.sort?.order;
  const search = params.search;
  const requestParams = {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: sortField,
    sortOrder,
    search,
    visibility,
    knownTotal: params.knownTotal,
  };
  const response = await listOrganizationGroupedAlerts(
    apiClient,
    organizationId,
    requestParams,
    { signal }
  );

  const isTableFormat = isTableEngineResponse(response);
  if (isTableFormat) {
    return response as unknown as DataFetchResponse<GroupedAlert>;
  }
  // this should never happen when data table feature flag is enabled
  if (isLegacyAlertsResponse(response)) {
    return transformLegacyToDataTableRespponse(response, params.filters);
  }
  return {
    data: [],
    meta: { activeFilters: [] },
    pagination: { page: 1, limit: 20, total: 0, hasMore: false },
  };
}

/**
 * Fetch filter options for a specific field (e.g., 'type')
 * Returns array of string values that will be transformed by labelFormatter
 * Supports optional search parameter for server-side filtering
 */
export async function fetchAlertFilterOptions(
  organizationId: string,
  field: AlertFilterOptionField,
  params: FilterOptionsParams
): Promise<string[]> {
  const response = await getOrganizationAlertFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    }
  );

  return response.options;
}

export async function fetchAlertFileOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<{
  options: string[];
  total?: number;
  nextOffset?: number | null;
  hasMore?: boolean;
}> {
  const response = await getOrganizationAlertFilterOptions(
    apiClient,
    organizationId,
    {
      field: 'filePath',
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    }
  );

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/**
 * Generate query key for filter options
 * Returns a consistent query key array for React Query caching
 */
export function getFilterOptionsQueryKey(
  organizationId: string,
  field: AlertFilterOptionField,
  params: { search?: string; limit?: number }
): string[] {
  return [
    'organizations',
    organizationId,
    'alerts',
    'filter-options',
    field,
    ...(params.search ? [params.search] : []),
    ...(params.limit ? [String(params.limit)] : []),
  ];
}

/**
 * Fetch organization members for filter options
 * Returns array of member objects with id, name, and email
 * Supports client-side search filtering
 */
export async function fetchOrganizationMembersForFilter(
  organizationId: string,
  params: FilterOptionsParams
): Promise<
  Array<{
    id: string;
    name: string | null;
    email: string | null;
    userType: string | null;
  }>
> {
  // Fetch all org members and the set of user IDs that actually have findings
  // in parallel. Only members in the findings set should appear in the filter
  // (item 4: filter options must reflect data that exists in the table).
  const [members, alertUsersResponse] = await Promise.all([
    listOrganizationMembers(apiClient, organizationId),
    getOrganizationAlertFilterOptions(apiClient, organizationId, {
      field: 'users',
      limit: 1000,
    }),
  ]);

  const usersWithAlerts = new Set(alertUsersResponse.options);

  // Map to our internal format, scoped to members who have findings
  let filteredMembers = members
    .filter(
      member => member.internalId && usersWithAlerts.has(member.internalId)
    )
    .map(m => ({
      id: m.internalId as string,
      name: m.name,
      email: m.email,
      // KIR-3908: carry the row's kind so the option label can resolve to
      // "Discovered"/"Unattributed" instead of leaking the internal UUID (these
      // placeholder rows have no name/email).
      userType: m.userType ?? null,
    }));

  // Apply client-side search filtering if search param is provided
  if (params.search) {
    const searchLower = params.search.toLowerCase();
    filteredMembers = filteredMembers.filter(m => {
      const nameMatch = m.name?.toLowerCase().includes(searchLower);
      const emailMatch = m.email?.toLowerCase().includes(searchLower);
      return nameMatch || emailMatch;
    });
  }

  return filteredMembers;
}

/**
 * Fetch organization members formatted as filter options with metadata
 * Returns array of filter option objects ready for rendering
 */
export async function fetchMemberFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<
  Array<{ value: string; label: string; metadata: Record<string, unknown> }>
> {
  const members = await fetchOrganizationMembersForFilter(
    organizationId,
    params
  );
  const options = members.map(m => ({
    value: m.id,
    // KIR-3908: resolve via getUserDisplayName so the Unattributed placeholder
    // (no name/email) renders "Unattributed" instead of its raw internal UUID —
    // the same fallback rule the Users cell uses. Discovered rows carry a real
    // email, so they surface it.
    label: getUserDisplayName({
      id: m.id,
      name: m.name,
      email: m.email,
      userType: m.userType,
    }),
    metadata: {
      id: m.id,
      name: m.name,
      email: m.email,
      userType: m.userType,
    },
  }));

  // KIR-3908: a single "Discovered" value that matches ALL discovered users
  // (people acting through the Cowork hook with no Kirin account). Shown when
  // the search is empty or matches "discovered". Individual discovered users
  // also appear as their own options above (they're real users with findings).
  const searchLower = params.search?.toLowerCase() ?? '';
  if (!searchLower || 'discovered'.includes(searchLower)) {
    options.unshift({
      value: DISCOVERED_USERS_FILTER_VALUE,
      label: 'Discovered',
      metadata: {
        id: DISCOVERED_USERS_FILTER_VALUE,
        name: 'Discovered',
        email: null,
        userType: null,
      },
    });
  }

  return options;
}

/**
 * Generate query key for user filter options
 */
export function getUserFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): string[] {
  return [
    'organizations',
    organizationId,
    'members',
    'filter-options',
    ...(params.search ? [params.search] : []),
  ];
}
